/**
 * Cross-project pattern library.
 *
 * Patterns are stored under ~/.cai/patterns/ (or $CAI_HOME/patterns/), shared
 * across all projects on this machine. The user explicitly opts in by running
 * `cai pattern share <name>` — nothing is uploaded automatically.
 *
 * Each shared pattern has:
 *   - <hash>.md       — the original pattern content
 *   - <hash>.meta.json — stack hints, source, dependencies, tags, timestamps
 *
 * The hash is content-based (sha1 of name + content) so re-sharing the same
 * pattern is idempotent and renames don't create duplicates.
 */
import { existsSync, mkdirSync, readFileSync, readdirSync, writeFileSync, statSync } from "node:fs";
import { homedir } from "node:os";
import { join, basename } from "node:path";
import { createHash } from "node:crypto";
import type { ProjectModel } from "../types.js";

export interface PatternMeta {
  hash: string;
  name: string;
  description: string;
  /** Source manifest type — used for stack matching */
  stack: string | null;
  /** Top-level dependencies from the source project at share time */
  dependencies: string[];
  /** Project root the pattern was shared from (for traceability only) */
  source: string;
  createdAt: string;
  /** Sequential version number for this name (1, 2, 3, …). Defaults to 1 for legacy entries. */
  version?: number;
  /** Hashes of older versions of this pattern, oldest first. */
  previousHashes?: string[];
}

export interface LibraryEntry extends PatternMeta {
  contentPath: string;
}

/** Resolve the library root. Defaults to ~/.cai but respects $CAI_HOME. */
export function libraryRoot(): string {
  return process.env.CAI_HOME ?? join(homedir(), ".cai");
}

function patternsDir(): string {
  return join(libraryRoot(), "patterns");
}

function ensureDir(): void {
  mkdirSync(patternsDir(), { recursive: true });
}

/** Hash a pattern by name + content for stable, idempotent IDs. */
function hashPattern(name: string, content: string): string {
  return createHash("sha1").update(`${name}\n${content}`).digest("hex").slice(0, 12);
}

/** Extract a one-line description from the pattern's frontmatter or first heading. */
function extractDescription(content: string): string {
  const fmMatch = content.match(/^---[\s\S]*?description:\s*(.+?)\s*\n[\s\S]*?---/);
  if (fmMatch) return fmMatch[1].replace(/^["']|["']$/g, "");
  const h1Match = content.match(/^#\s+(.+)$/m);
  return h1Match ? h1Match[1] : "(no description)";
}

/**
 * Add a pattern from a local file to the global library.
 *
 * Idempotent: if the exact same content was already shared, returns the
 * existing entry unchanged.
 *
 * Versioned: if a pattern with the same `name` exists but with different
 * content, this becomes a new version (v2, v3, …) and the previous hashes
 * are tracked in `previousHashes`. Old versions are not deleted —
 * `cai pattern install <hash>` can still pull them.
 */
export function addToLibrary(
  patternPath: string,
  project: ProjectModel,
  sourceProjectRoot: string
): LibraryEntry {
  if (!existsSync(patternPath)) {
    throw new Error(`Pattern file not found: ${patternPath}`);
  }
  ensureDir();

  const content = readFileSync(patternPath, "utf8");
  const name = basename(patternPath, ".md");
  const hash = hashPattern(name, content);

  const contentPath = join(patternsDir(), `${hash}.md`);
  const metaPath = join(patternsDir(), `${hash}.meta.json`);

  // Idempotency: same name + same content already shared
  if (existsSync(contentPath)) {
    return readEntry(hash);
  }

  // Look for older versions of the same pattern (different content, same name)
  const existingVersions = listVersions(name);
  const latestVersion = existingVersions.length > 0
    ? Math.max(...existingVersions.map((e) => e.version ?? 1))
    : 0;
  const previousHashes = existingVersions
    .sort((a, b) => (a.version ?? 1) - (b.version ?? 1))
    .map((e) => e.hash);

  const meta: PatternMeta = {
    hash,
    name,
    description: extractDescription(content),
    stack: project.rootManifest?.type ?? null,
    dependencies: collectDependencies(project),
    source: sourceProjectRoot,
    createdAt: new Date().toISOString(),
    version: latestVersion + 1,
    previousHashes: previousHashes.length > 0 ? previousHashes : undefined,
  };

  writeFileSync(contentPath, content, "utf8");
  writeFileSync(metaPath, JSON.stringify(meta, null, 2) + "\n", "utf8");

  return { ...meta, contentPath };
}

/** All entries with the given pattern name, regardless of version. */
export function listVersions(name: string): LibraryEntry[] {
  return listLibrary().filter((e) => e.name === name);
}

/** Latest version of a pattern by name, or null if none exists. */
export function latestVersion(name: string): LibraryEntry | null {
  const versions = listVersions(name);
  if (versions.length === 0) return null;
  return versions.sort((a, b) => (b.version ?? 1) - (a.version ?? 1))[0];
}

/** Read all entries in the library, sorted by creation date descending. */
export function listLibrary(): LibraryEntry[] {
  if (!existsSync(patternsDir())) return [];

  const entries: LibraryEntry[] = [];
  for (const file of readdirSync(patternsDir())) {
    if (!file.endsWith(".meta.json")) continue;
    try {
      const meta = JSON.parse(readFileSync(join(patternsDir(), file), "utf8")) as PatternMeta;
      entries.push({ ...meta, contentPath: join(patternsDir(), `${meta.hash}.md`) });
    } catch {
      // skip malformed meta
    }
  }
  return entries.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Read a single entry by its hash. Throws if it doesn't exist. */
export function readEntry(hash: string): LibraryEntry {
  const metaPath = join(patternsDir(), `${hash}.meta.json`);
  const contentPath = join(patternsDir(), `${hash}.md`);
  if (!existsSync(metaPath) || !existsSync(contentPath)) {
    throw new Error(`Library entry not found: ${hash}`);
  }
  const meta = JSON.parse(readFileSync(metaPath, "utf8")) as PatternMeta;
  return { ...meta, contentPath };
}

/** Read the raw content of a library entry. */
export function readEntryContent(hash: string): string {
  return readFileSync(readEntry(hash).contentPath, "utf8");
}

/**
 * Find an entry by hash prefix or by name.
 * For names with multiple versions, returns the latest (highest version number).
 */
export function findEntry(query: string): LibraryEntry | null {
  const all = listLibrary();
  const byHashPrefix = all.filter((e) => e.hash.startsWith(query));
  if (byHashPrefix.length === 1) return byHashPrefix[0];

  const byName = all.filter((e) => e.name === query);
  if (byName.length === 0) return null;
  // Return the highest version (or most recent createdAt as tiebreaker)
  return byName.sort((a, b) => {
    const va = a.version ?? 1;
    const vb = b.version ?? 1;
    if (vb !== va) return vb - va;
    return b.createdAt.localeCompare(a.createdAt);
  })[0];
}

/** Collect top-level dependencies (root + workspaces) for matching. */
export function collectDependencies(project: ProjectModel): string[] {
  const deps = new Set<string>();
  if (project.rootManifest) {
    Object.keys(project.rootManifest.dependencies).forEach((d) => deps.add(d));
    Object.keys(project.rootManifest.devDependencies).forEach((d) => deps.add(d));
  }
  for (const ws of project.workspaces) {
    Object.keys(ws.manifest.dependencies).forEach((d) => deps.add(d));
    Object.keys(ws.manifest.devDependencies).forEach((d) => deps.add(d));
  }
  return Array.from(deps).sort();
}

/** Get library size in bytes — used by `cai pattern library --stats`. */
export function libraryStats(): { entries: number; bytes: number } {
  if (!existsSync(patternsDir())) return { entries: 0, bytes: 0 };
  let bytes = 0;
  let entries = 0;
  for (const file of readdirSync(patternsDir())) {
    try {
      bytes += statSync(join(patternsDir(), file)).size;
      if (file.endsWith(".meta.json")) entries++;
    } catch {
      // ignore
    }
  }
  return { entries, bytes };
}
